import { Link } from 'react-router-dom';
import { aboutInfo, faqs } from '@/mocks/navigation';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function About() {
  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        type="Organization"
        data={{
          name: aboutInfo.brandName,
          description: aboutInfo.mission,
          foundingDate: aboutInfo.foundedYear,
          url: 'https://www.caoliu-dh.com',
          contactPoint: {
            '@type': 'ContactPoint',
            email: aboutInfo.contact.email,
            contactType: 'customer service',
          },
        }}
      />

      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />

      <div className="container-custom py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="mt-6 text-center max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-5xl font-bold text-foreground-950">
            关于我们
          </h1>
          <p className="mt-4 text-lg text-foreground-600 leading-relaxed">
            {aboutInfo.mission}
          </p>
        </section>

        {/* Brand Intro */}
        <section className="mt-12 max-w-3xl mx-auto">
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-10">
            <h2 className="text-2xl font-bold text-foreground-950">品牌介绍</h2>
            <p className="mt-4 text-base text-foreground-700 leading-relaxed">
              {aboutInfo.intro}
            </p>
            <p className="mt-4 text-base text-foreground-700 leading-relaxed">
              我们深知用户在访问过程中遇到的各种困难——域名频繁变更、访问不稳定、仿冒网站泛滥等问题。
              因此，我们建立了一套完善的网址监控和验证体系，确保每一个推荐的入口都经过严格检测，
              让用户可以放心使用。
            </p>
            <p className="mt-4 text-base text-foreground-700 leading-relaxed">
              截至目前，我们的导航平台已收录超过100个草榴社区相关入口地址，
              覆盖主站、镜像站、备用入口等多种类型，服务覆盖全国及海外用户。
              我们承诺：永久免费、持续更新、安全可靠。
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="mt-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground-950 text-center">我们的优势</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {aboutInfo.features.map((feature, idx) => (
              <div
                key={idx}
                className="flex gap-4 p-5 bg-background-50 rounded-xl border border-background-200/70"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${feature.icon} text-lg text-primary-600`}></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900">{feature.title}</h3>
                  <p className="mt-1 text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground-950 text-center">联系我们</h2>
          <p className="mt-2 text-sm text-foreground-500 text-center">
            如果您有任何问题、建议或发现失效链接，欢迎通过以下方式联系我们
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-5 bg-background-50 rounded-xl border border-background-200/70 text-center">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
                <i className="ri-mail-line text-lg text-primary-600"></i>
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-900">电子邮箱</h3>
              <p className="mt-1 text-sm text-foreground-500">{aboutInfo.contact.email}</p>
            </div>
            <div className="p-5 bg-background-50 rounded-xl border border-background-200/70 text-center">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto">
                <i className="ri-wechat-line text-lg text-accent-600"></i>
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-900">微信</h3>
              <p className="mt-1 text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
            </div>
            <div className="p-5 bg-background-50 rounded-xl border border-background-200/70 text-center">
              <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto">
                <i className="ri-qq-line text-lg text-secondary-600"></i>
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground-900">QQ</h3>
              <p className="mt-1 text-sm text-foreground-500">{aboutInfo.contact.qq}</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground-950 text-center">常见问题</h2>
          <div className="mt-6 space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <h3 className="text-sm font-semibold text-foreground-900 pr-4">
                    {faq.question}
                  </h3>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 group-open:rotate-45 transition-transform duration-200">
                    <i className="ri-add-line text-sm"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Back */}
        <div className="mt-12 text-center pb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}